import { uploadAllSlots } from "@/client/api/upload";
import { isRecord } from "@/client/utils/typeGuards";
import {
  normalizeMediaInput,
  type UploadFileItem,
  type NormalizedMediaItem,
} from "@/features/canvas/services/mediaDataContract";

type InlineData = { mime_type: string; data: string };

export interface FormTransformContext {
  fieldId?: string;
  values: Record<string, unknown>;
  options?: Record<string, unknown>;
}

export const createTransformContext = (
  fieldId: string,
  values: object,
  options?: Record<string, unknown>
): FormTransformContext => ({
  fieldId,
  values: values as Record<string, unknown>,
  options,
});

const isInlineDataObject = (value: unknown): value is InlineData => {
  if (!isRecord(value) || Array.isArray(value)) return false;
  const mime = (value as { mime_type?: unknown }).mime_type;
  const data = (value as { data?: unknown }).data;
  return typeof mime === "string" && typeof data === "string" && mime.trim() !== "" && data.trim() !== "";
};

const BASE64_REGEX = /^[A-Za-z0-9+/]+={0,2}$/;

const encodeStringToBase64 = (value: string): string | null => {
  try {
    if (typeof btoa === "function") {
      return btoa(value);
    }
  } catch {
    // ignore
  }
  return null;
};

const parseDataUrlToInlineData = (raw: string, mimeHint?: string): InlineData | null => {
  const trimmed = raw.trim();
  if (!trimmed.toLowerCase().startsWith("data:")) return null;

  const commaIndex = trimmed.indexOf(",");
  if (commaIndex < 0) return null;

  const meta = trimmed.slice(5, commaIndex);
  const dataPart = trimmed.slice(commaIndex + 1);
  if (!dataPart) return null;

  const metaParts = meta.split(";");
  const mimeType = metaParts[0]?.trim() || mimeHint?.trim() || "application/octet-stream";
  const isBase64 = metaParts.slice(1).some((part) => part.toLowerCase() === "base64");

  if (isBase64) {
    return { mime_type: mimeType, data: dataPart };
  }

  try {
    const decoded = decodeURIComponent(dataPart);
    const encoded = encodeStringToBase64(decoded);
    return encoded ? { mime_type: mimeType, data: encoded } : null;
  } catch {
    return null;
  }
};

const parseBase64StringToInlineData = (raw: string, mimeHint?: string): InlineData | null => {
  const trimmed = raw.trim();
  if (!trimmed || trimmed.toLowerCase().startsWith("data:")) return null;
  if (trimmed.length < 16 || !BASE64_REGEX.test(trimmed)) return null;
  return { mime_type: mimeHint?.trim() || "application/octet-stream", data: trimmed };
};

const inferMimeTypeFromUrl = (raw: string): string | undefined => {
  const url = raw.trim();
  if (!url) return undefined;
  const lower = url.toLowerCase();
  if (lower.startsWith("data:")) return undefined;

  const withoutQuery = url.split("?")[0]?.split("#")[0] ?? url;
  const match = withoutQuery.toLowerCase().match(/\.([a-z0-9]+)$/);
  const ext = match?.[1];
  if (!ext) return undefined;

  switch (ext) {
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "webp":
      return "image/webp";
    case "gif":
      return "image/gif";
    case "bmp":
      return "image/bmp";
    case "svg":
      return "image/svg+xml";
    case "tif":
    case "tiff":
      return "image/tiff";
    case "avif":
      return "image/avif";
    default:
      return undefined;
  }
};

const coerceInlineData = (candidate: unknown): InlineData | null => {
  if (typeof candidate === "string") {
    return parseDataUrlToInlineData(candidate) ?? parseBase64StringToInlineData(candidate);
  }

  if (!isRecord(candidate) || Array.isArray(candidate)) {
    return null;
  }

  const inlineDataField = (candidate as Record<string, unknown>).inline_data;
  if (isInlineDataObject(inlineDataField)) {
    return inlineDataField;
  }

  if (isInlineDataObject(candidate)) {
    return candidate;
  }

  const mimeHint = typeof (candidate as Record<string, unknown>).mime_type === "string"
    ? ((candidate as Record<string, unknown>).mime_type as string)
    : undefined;

  const data = (candidate as Record<string, unknown>).data;
  if (typeof data === "string" && mimeHint) {
    return { mime_type: mimeHint.trim(), data };
  }

  const src = (candidate as Record<string, unknown>).src;
  if (typeof src === "string") {
    return parseDataUrlToInlineData(src, mimeHint) ?? parseBase64StringToInlineData(src, mimeHint);
  }

  return null;
};

/**
 * 将标准化媒体项转为 UploadFileItem 格式
 * 内部调用 mediaDataContract.normalizeMediaInput
 */
const normalizeUploadItems = (value: unknown): UploadFileItem[] => {
  const items = normalizeMediaInput(value);
  return items.map((item: NormalizedMediaItem) => {
    switch (item.source.type) {
      case "file":
        return { id: item.id, file: item.source.file };
      case "url":
        return { id: item.id, src: item.source.url };
      case "cache":
        return { id: item.id, cacheRef: item.source.ref };
    }
  });
};

const readAsDataURL = (f: File | Blob): Promise<string> =>
  new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => {
      const result = fr.result as string;
      const idx = result.indexOf(",");
      const base64 = idx >= 0 ? result.slice(idx + 1) : result;
      resolve(base64);
    };
    fr.onerror = (e) => reject(e);
    fr.readAsDataURL(f);
  });

export const fileToBase64Object = async (
  value: unknown
): Promise<{ mime_type: string; data: string } | null> => {
  const items = normalizeUploadItems(value);
  if (items.length === 0) return null;
  const first = items[0];
  const file = first?.file;
  if (!file) return null;

  const base64 = await readAsDataURL(file);
  return {
    mime_type: file.type || "application/octet-stream",
    data: base64,
  };
};

/**
 * 将媒体输入转为 inline_data 格式数组
 * 重构后：统一通过解析层获取数据
 */
export const filesToInlineDataList = async (
  value: unknown,
  opts?: { limit?: number }
): Promise<Array<{ mime_type: string; data: string }>> => {
  const limit = opts?.limit && Number.isFinite(opts.limit) ? Math.max(1, Math.round(opts.limit)) : undefined;

  // 1) 单值快速路径：支持 inline_data / data URL / base64 / 纯 URL / UploadFileItem / File / cacheRef
  if (!Array.isArray(value)) {
    const directInlineData = coerceInlineData(value);
    if (directInlineData) return [directInlineData];

    const items = normalizeMediaInput(value, limit);
    if (items.length === 0) return [];

    const { resolveAllMediaItems } = await import(
      "@/features/canvas/services/mediaDataContract"
    );
    const resolved = await resolveAllMediaItems(items, { downloadUrls: true });

    const results: Array<{ mime_type: string; data: string }> = [];
    for (const item of resolved) {
      if (typeof limit === "number" && results.length >= limit) break;
      if (!item.file) {
        if (item.url) {
          console.warn("[filesToInlineDataList] 无法下载媒体 URL:", item.url);
        }
        continue;
      }

      const base64 = await readAsDataURL(item.file);
      results.push({
        mime_type: item.mimeType || (item.url ? inferMimeTypeFromUrl(item.url) : undefined) || "application/octet-stream",
        data: base64,
      });
    }
    return results;
  }

  // 2. 优先检查原始输入中的 inline_data（数组场景）
  // normalizeMediaInput 不会识别 inline_data 对象，需要单独处理
  const rawArray = Array.isArray(value) ? value : [];
  const candidates = typeof limit === "number" ? rawArray.slice(0, limit) : rawArray;

  const inlineResults: Array<{ mime_type: string; data: string }> = [];
  const processedIndices = new Set<number>();
  for (let i = 0; i < candidates.length; i++) {
    const inlineData = coerceInlineData(candidates[i]);
    if (inlineData) {
      inlineResults.push(inlineData);
      processedIndices.add(i);
    }
  }

  // 当 limit 已被 inline_data 占满时直接返回
  if (typeof limit === "number" && inlineResults.length >= limit) {
    return inlineResults.slice(0, limit);
  }

  const remainingLimit = typeof limit === "number" ? Math.max(limit - inlineResults.length, 0) : undefined;

  // 3. 规范化（使用剩余名额），排除已被 coerceInlineData 处理的项目
  const remainingItems = rawArray.filter((_, idx) => !processedIndices.has(idx));
  const items = normalizeMediaInput(remainingItems, remainingLimit);

  // 4. 没有可解析的媒体时，直接返回已有 inline_data
  if (items.length === 0) return inlineResults;

  // 5. 导入解析层
  const { resolveAllMediaItems } = await import(
    "@/features/canvas/services/mediaDataContract"
  );

  // 6. 统一解析（下载 URL）
  const resolved = await resolveAllMediaItems(items, { downloadUrls: true });

  // 7. 格式转换
  const results: Array<{ mime_type: string; data: string }> = [...inlineResults];
  for (const item of resolved) {
    if (typeof limit === "number" && results.length >= limit) break;
    if (!item.file) {
      if (item.url) {
        console.warn("[filesToInlineDataList] 无法下载媒体 URL:", item.url);
      }
      continue;
    }

    const base64 = await readAsDataURL(item.file);
    results.push({
      mime_type: item.mimeType || (item.url ? inferMimeTypeFromUrl(item.url) : undefined) || "application/octet-stream",
      data: base64,
    });
  }
  return typeof limit === "number" ? results.slice(0, limit) : results;
};

export const extractUrlsFromUpload = async (value: unknown): Promise<string[]> => {
  // 1. 规范化
  const items = normalizeMediaInput(value);
  if (items.length === 0) return [];

  // 2. 导入解析层
  const { resolveAllMediaItems } = await import(
    "@/features/canvas/services/mediaDataContract"
  );

  // 3. 统一解析
  const resolved = await resolveAllMediaItems(items);

  // 4. 检查无效项
  const hasInvalidBlobUrl = resolved.some(
    (item) => item.url?.startsWith("blob:") && !item.file
  );
  if (hasInvalidBlobUrl) {
    throw new Error("图片引用无效，请重新选择或重新上传图片");
  }

  // 5. 上传获取 URL - 将 Blob 包装成 File
  const slots = resolved.map((item, index) => {
    let file: File | undefined;
    if (item.file) {
      if (item.file instanceof File) {
        file = item.file;
      } else {
        // Blob 需要包装成 File，保留 mime 类型
        const mimeType = item.mimeType || item.file.type || "image/png";
        const ext = mimeType.split("/")[1] || "png";
        file = new File([item.file], `media_${Date.now()}_${index}.${ext}`, { type: mimeType });
      }
    }
    return {
      id: item.id ?? "",
      src: item.url ?? "",
      file,
    };
  });
  return uploadAllSlots(slots);
};

export const extractFirstUrlFromUpload = async (
  value: unknown
): Promise<string | undefined> => {
  const urls = await extractUrlsFromUpload(value);
  if (!Array.isArray(urls) || urls.length === 0) {
    return undefined;
  }
  return urls[0];
};

/**
 * 从上传值中提取第一个媒体并转为 base64 data URL 格式
 * 返回格式：data:{MIME_type};base64,{base64_data}
 * 用于 API 直接接受 base64 编码图像的场景（如阿里百炼 Wan 模型）
 */
export const extractFirstBase64DataUrl = async (
  value: unknown
): Promise<string | undefined> => {
  const inlineDataList = await filesToInlineDataList(value, { limit: 1 });
  if (inlineDataList.length === 0) {
    return undefined;
  }
  const first = inlineDataList[0];
  return `data:${first.mime_type};base64,${first.data}`;
};

/**
 * 从上传值中提取第一个 File 对象（用于 multipart 表单提交）
 * - 优先使用已有的 file 对象
 * - 其次从 cacheRef 读取 IndexedDB 数据并转为 File
 * - 最后从 src URL 获取并转为 File
 */
export const extractFirstFileFromUpload = async (
  value: unknown
): Promise<File | undefined> => {
  const items = normalizeUploadItems(value);
  if (items.length === 0) {
    return undefined;
  }

  const first = items[0];

  // 优先使用已有的 file 对象
  if (first.file) {
    return first.file as File;
  }

  // 从 cacheRef 读取 IndexedDB 数据
  if (first.cacheRef && first.cacheRef.status === "cached") {
    try {
      const { MediaCacheStore } = await import("@/features/canvas/services/mediaCacheStore");
      const cacheEntry = await MediaCacheStore.get(first.cacheRef.cacheId);
      if (cacheEntry && cacheEntry.data) {
        const mimeType = first.cacheRef.mimeType || cacheEntry.mimeType || "image/png";
        return new File([cacheEntry.data], `cached_${Date.now()}.png`, { type: mimeType });
      }
    } catch (error) {
      console.warn("[extractFirstFileFromUpload] 从缓存读取失败:", error);
    }
  }

  // 从 src URL 获取并转为 File
  if (typeof first.src === "string" && first.src.trim()) {
    const trimmedSrc = first.src.trim();
    const lowerSrc = trimmedSrc.toLowerCase();

    // 处理 data URL
    if (lowerSrc.startsWith("data:")) {
      try {
        const response = await fetch(trimmedSrc);
        const blob = await response.blob();
        return new File([blob], `image_${Date.now()}.png`, { type: blob.type || "image/png" });
      } catch (error) {
        console.warn("[extractFirstFileFromUpload] data URL 转换失败:", error);
      }
    }

    // 处理 http/https/blob URL
    if (lowerSrc.startsWith("http://") || lowerSrc.startsWith("https://") || lowerSrc.startsWith("blob:")) {
      try {
        const response = await fetch(trimmedSrc);
        const blob = await response.blob();
        return new File([blob], `image_${Date.now()}.png`, { type: blob.type || "image/png" });
      } catch (error) {
        console.warn("[extractFirstFileFromUpload] URL 获取失败:", error);
      }
    }
  }

  return undefined;
};

export const extractUrlListOrUndefined = async (
  value: unknown
): Promise<string[] | undefined> => {
  const urls = await extractUrlsFromUpload(value);
  if (!Array.isArray(urls) || urls.length === 0) {
    return undefined;
  }
  return urls;
};

export const ensureMultiMaxImages = (
  value: unknown,
  context?: FormTransformContext
): number | undefined => {
  const values = (context?.values ?? {}) as Record<string, unknown>;
  const mode = typeof values.mode === "string" ? (values.mode as string) : "";
  if (mode !== "multi_image") {
    return undefined;
  }
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return undefined;
  }
  const clamped = Math.max(1, Math.min(15, Math.round(num)));
  return clamped;
};

export const mapBoolean = (value: unknown, context?: FormTransformContext): unknown => {
  if (value !== true && value !== false) {
    return value;
  }
  const options = context?.options;
  if (!isRecord(options)) {
    return value ? true : false;
  }
  const trueValue = (options as { true?: unknown }).true;
  const falseValue = (options as { false?: unknown }).false;
  if (value === true) {
    return trueValue !== undefined ? trueValue : true;
  }
  return falseValue !== undefined ? falseValue : false;
};

const parseResolutionText = (raw: unknown): { width: number; height: number } | null => {
  if (typeof raw !== "string") {
    return null;
  }
  const match = raw.trim().match(/^(\d+)\s*[xX]\s*(\d+)$/);
  if (!match) {
    return null;
  }
  const width = Number.parseInt(match[1], 10);
  const height = Number.parseInt(match[2], 10);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }
  return { width, height };
};

const categorizeReplicateSeedreamSize = (width: number, height: number): "1K" | "2K" | "4K" | "custom" => {
  const longest = Math.max(width, height);
  const isCloseTo = (value: number, target: number) => Math.abs(value - target) <= 64;
  if (isCloseTo(longest, 1024)) return "1K";
  if (isCloseTo(longest, 2048)) return "2K";
  if (isCloseTo(longest, 4096)) return "4K";
  return "custom";
};

export const replicateSeedreamSizeFromResolution = (
  value: unknown
): "1K" | "2K" | "4K" | "custom" => {
  const parsed = parseResolutionText(value);
  if (!parsed) {
    return "custom";
  }
  return categorizeReplicateSeedreamSize(parsed.width, parsed.height);
};

export const replicateSeedreamWidthFromResolution = (
  _value: unknown,
  context?: FormTransformContext
): number | undefined => {
  const values = (context?.values ?? {}) as Record<string, unknown>;
  const resolutionRaw = values.size;
  const parsed = parseResolutionText(resolutionRaw);
  if (!parsed) {
    return undefined;
  }
  const category = categorizeReplicateSeedreamSize(parsed.width, parsed.height);
  if (category !== "custom") {
    return undefined;
  }
  return parsed.width;
};

export const replicateSeedreamHeightFromResolution = (
  _value: unknown,
  context?: FormTransformContext
): number | undefined => {
  const values = (context?.values ?? {}) as Record<string, unknown>;
  const resolutionRaw = values.size;
  const parsed = parseResolutionText(resolutionRaw);
  if (!parsed) {
    return undefined;
  }
  const category = categorizeReplicateSeedreamSize(parsed.width, parsed.height);
  if (category !== "custom") {
    return undefined;
  }
  return parsed.height;
};

const splitLines = (raw: unknown): string[] => {
  if (typeof raw !== "string") return [];
  return raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
};

const styleLabelMap: Record<string, string> = {
  cinematic: "cinematic, filmic lighting, strong grading",
  documentary: "documentary realism with natural lighting",
  anime: "stylised animation inspired by Japanese anime",
  fashion: "high-fashion editorial aesthetic",
  scifi: "futuristic sci-fi worldbuilding",
};

const cameraLabelMap: Record<string, string> = {
  dynamic: "dynamic tracking with deliberate movement",
  static: "locked-off tripod shot",
  handheld: "handheld documentary shakiness",
  drone: "aerial drone perspective",
  orbit: "rotational orbiting camera move",
};

const audioLabelMap: Record<string, string> = {
  cinematic: "cinematic orchestral score",
  ambient: "ambient environmental sound design",
  dialogue: "dialogue-forward audio mix",
  silent: "deliver silent video output",
};

export const composeSoraMessages = (
  value: unknown,
  context?: FormTransformContext
): Array<{ role: string; content: string }> | undefined => {
  const prompt = typeof value === "string" ? value.trim() : "";
  if (!prompt) {
    return undefined;
  }

  const values = (context?.values ?? {}) as Record<string, unknown>;
  const systemPrompt = typeof values.systemPrompt === "string" ? values.systemPrompt.trim() : "";

  const directives: string[] = [];
  const duration = typeof values.duration === "string" ? values.duration : undefined;
  if (duration) directives.push(`Target duration: ${duration}`);

  const aspectRatio = typeof values.aspectRatio === "string" ? values.aspectRatio : undefined;
  if (aspectRatio) directives.push(`Aspect ratio: ${aspectRatio}`);

  const stylePreset = typeof values.stylePreset === "string" ? values.stylePreset : undefined;
  if (stylePreset) directives.push(`Visual style preference: ${styleLabelMap[stylePreset] ?? stylePreset}`);

  const cameraMotion = typeof values.cameraMotion === "string" ? values.cameraMotion : undefined;
  if (cameraMotion) directives.push(`Camera movement: ${cameraLabelMap[cameraMotion] ?? cameraMotion}`);

  const audioProfile = typeof values.audioProfile === "string" ? values.audioProfile : undefined;
  if (audioProfile) directives.push(`Audio direction: ${audioLabelMap[audioProfile] ?? audioProfile}`);

  const negative = typeof values.negativePrompt === "string" ? values.negativePrompt.trim() : "";
  if (negative) directives.push(`Avoid: ${negative}`);

  const storyBeats = splitLines(values.storyBeats);
  const referenceNotes = splitLines(values.referenceNotes);

  const sections: string[] = [prompt];

  if (directives.length > 0) {
    sections.push(`Video directives:\n- ${directives.join("\n- ")}`);
  }

  if (storyBeats.length > 0) {
    sections.push(`Story beats:\n- ${storyBeats.join("\n- ")}`);
  }

  if (referenceNotes.length > 0) {
    sections.push(`Reference materials:\n- ${referenceNotes.join("\n- ")}`);
  }

  const userContent = sections.join("\n\n");

  const messages: Array<{ role: string; content: string }> = [];
  if (systemPrompt) {
    messages.push({ role: "system", content: systemPrompt });
  }
  messages.push({ role: "user", content: userContent });

  return messages;
};

/**
 * 将上传控件中的第一个视频文件转为 Base64 编码。
 * 返回去除 data URL 前缀后的纯 Base64 字符串。
 */
export const fileToBase64Video = async (
  value: unknown
): Promise<{ mime_type: string; data: string } | null> => {
  const items = normalizeUploadItems(value);
  if (items.length === 0) return null;

  const first = items[0];
  const file = first?.file;
  if (!file) return null;

  const readAsDataURLLocal = (f: Blob) =>
    new Promise<string>((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => {
        const result = fr.result as string;
        const idx = result.indexOf(",");
        const base64 = idx >= 0 ? result.slice(idx + 1) : result;
        resolve(base64);
      };
      fr.onerror = (e) => reject(e);
      fr.readAsDataURL(f);
    });

  try {
    const base64 = await readAsDataURLLocal(file);
    return {
      mime_type: file.type || "video/mp4",
      data: base64,
    };
  } catch (error) {
    console.error("视频文件转为 Base64 失败", error);
    return null;
  }
};
